import React, { useRef } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import RideForm from './RideForm';

const TITLES = {
  create: 'Neue Fahrt anlegen',
  edit: 'Fahrt bearbeiten',
  assign: 'Fahrer zuweisen',
};

/**
 * Globaler Dialog, der dieselbe RideForm im eingebetteten Modus rendert –
 * für Neu anlegen, Bearbeiten und Zuweisen. Ersetzt die frühere Vollseite.
 *
 * Props:
 *  - open, mode ('create'|'edit'|'assign'), rideId, draft
 *  - dialogKey: Zähler; erzwingt frisches Remount pro Öffnung (kein State-Leak)
 *  - onClose, onSuccess
 */
const RideFormDialog = ({ open, mode, rideId, draft, dialogKey, onClose, onSuccess }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // Die Kurzanleitung gehört der RideForm – dort wird auch F1 verarbeitet.
  // Der Header ruft sie nur auf.
  const helpRef = useRef(null);

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // Backdrop-Klick ignorieren – schützt vor versehentlichem Datenverlust bei
        // diesem großen Formular. Escape und der Schließen-Button funktionieren weiter.
        if (reason === 'backdropClick') return;
        onClose?.();
      }}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      scroll="paper"
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          {TITLES[mode] || 'Fahrt'}
          <Tooltip title="Tastaturbedienung – Kurzanleitung (F1)">
            <IconButton
              size="small"
              onClick={() => helpRef.current?.()}
              aria-label="Kurzanleitung zur Tastaturbedienung"
            >
              <HelpOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <IconButton onClick={() => onClose?.()} aria-label="Schließen" edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {open && (
          <RideForm
            key={dialogKey}
            embedded
            mode={mode}
            rideId={rideId}
            initialDraft={draft}
            helpRef={helpRef}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RideFormDialog;
